import type { Filter, FilterMatchMode } from "@/shared/components/ResourceView";

/**
 * Comparator allow-lists shared by the two admin tables' filter definitions.
 *
 * The admin search/feedback backends accept only positive, exact predicates on
 * these fields, so the OperatorPicker must be narrowed to the one comparator
 * each consumer honors:
 * - ``EQUALS_ONLY`` — opaque-id ``string`` filters (Session/``thread_id``) and
 *   the boolean pseudo-fields (``has_errors`` / ``is_customer``), which the
 *   structured admin search accepts only as ``EQUALS`` and 400s on the
 *   valueless ``EXISTS`` / ``NOT_EXISTS`` form; the feedback endpoint coerces
 *   any non-``EQUALS`` ``thread_id`` comparator to exact equality.
 * - ``CONTAINS_ONLY`` — the feedback table's enum filters, which the read-side
 *   mapper consumes only on ``CONTAINS`` (``NOT_CONTAINS`` has no exclusion
 *   surface and would silently return the complement).
 */
export const EQUALS_ONLY = ["EQUALS"] as const;
export const CONTAINS_ONLY = ["CONTAINS"] as const;

/**
 * Fields the admin thread search exposes as boolean pseudo-fields. They are
 * not columns on ``agent_sessions``; the backend extracts them out of the
 * condition tree and renders bespoke predicates, and only accepts them at the
 * top level of an AND group (see ``extract_boolean_pseudo_field_predicates``
 * in ``agent_thread_repo.py``).
 */
const PSEUDO_FIELDS: ReadonlySet<string> = new Set([
  "has_errors",
  "is_customer",
]);

/** True when any active filter targets an admin boolean pseudo-field. */
export function hasPseudoFieldFilter(filters: Filter[]): boolean {
  return filters.some((f) => PSEUDO_FIELDS.has(f.field));
}

/**
 * The match mode to submit with the admin thread search condition.
 *
 * An OR match mode wraps every user filter in an OR group, but the backend
 * rejects a pseudo-field (`has_errors` / `is_customer`) inside OR/NOT with a
 * 400. So coerce to `AND` whenever a pseudo-field filter is present; otherwise
 * honor the user's choice. Forcing AND keeps the pseudo-field at the top
 * level, where the backend accepts it.
 */
export function submittedMatchMode(
  filters: Filter[],
  userMatchMode: FilterMatchMode,
): FilterMatchMode {
  return hasPseudoFieldFilter(filters) ? "AND" : userMatchMode;
}
